import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { api } from '../lib/api.js';
import { Button, Tooltip, cn } from '../ui/index.js';

/**
 * The model-written brief, shown above the thread rather than instead of it.
 *
 * Three deliberate choices:
 *
 *   It sits NEXT TO its source. The thread is one glance below, so a summary
 *   that says something the ticket does not support is immediately checkable.
 *   A summary on its own screen would be trusted more than it deserves.
 *
 *   It is labelled as written by a model, with the model named. Not a
 *   disclaimer — an attribution, so nobody mistakes it for something a
 *   colleague wrote.
 *
 *   When there is no model it says so, in words, rather than rendering
 *   nothing. An empty space is indistinguishable from a broken feature.
 */
export interface Summary {
  status: 'ok' | 'unavailable' | 'failed';
  ask: string | null;
  done: string | null;
  next: string | null;
  raw: string | null;
  reason: string | null;
  cached: boolean;
  model: string | null;
  costMillicents: number | null;
  at: string | null;
}

export function TicketSummary({
  tenant,
  number,
  compact,
}: {
  tenant: string;
  number: number;
  /** The peek pane is narrow; the full page can breathe. */
  compact?: boolean;
}) {
  const [refreshing, setRefreshing] = useState(false);

  const q = useQuery({
    queryKey: ['ai-summary', tenant, number],
    queryFn: () => api.get<Summary>(`/api/v1/tenants/${tenant}/tickets/${number}/summary`),
    // The answer is cached server-side on the conversation fingerprint, so
    // there is nothing to gain from refetching on focus.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // AND NOT ON A TIMER. The QueryClient polls every 30s by default, which
    // is right for a queue somebody is watching and wrong for anything that
    // costs money to answer — `staleTime` does not stop it, because an
    // interval refetches on its own schedule whatever the staleness says.
    // An idle ticket page was buying a summary every thirty seconds.
    refetchInterval: false,
  });

  const refresh = async () => {
    setRefreshing(true);
    try {
      await q.refetch();
    } finally {
      setRefreshing(false);
    }
  };

  const s = q.data;

  return (
    <section
      className={cn(
        'rounded-md border border-brand-line/50 bg-brand-tint/30',
        compact ? 'px-3 py-2.5' : 'px-4 py-3',
      )}
      aria-label="Summary"
    >
      <header className="mb-2 flex items-center gap-1.5">
        <Sparkles aria-hidden className="h-3.5 w-3.5 text-brand" />
        <h3 className="text-2xs font-semibold uppercase tracking-wide text-ink-secondary">
          The short version
        </h3>
        {s?.cached && (
          <Tooltip content="Held from the last time this thread changed — no new model call">
            <span className="text-3xs text-ink-tertiary">cached</span>
          </Tooltip>
        )}
        <span className="ml-auto flex items-center gap-1.5">
          {s?.model && <span className="text-3xs text-ink-tertiary">{s.model}</span>}
          <Tooltip content="Write it again from the current thread">
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={() => void refresh()}
              disabled={q.isFetching || refreshing}
              aria-label="Rewrite the summary"
            >
              <RefreshCw
                aria-hidden
                className={cn('h-3 w-3', (q.isFetching || refreshing) && 'animate-spin')}
              />
            </Button>
          </Tooltip>
        </span>
      </header>

      {q.isLoading && (
        <p className="text-xs text-ink-tertiary">Reading the thread…</p>
      )}

      {q.error && (
        <p className="text-xs text-ink-tertiary">
          The summary could not be fetched. The ticket below is unaffected.
        </p>
      )}

      {s && s.status !== 'ok' && (
        <p className="text-xs leading-relaxed text-ink-tertiary">{s.reason}</p>
      )}

      {s && s.status === 'ok' && (
        <>
          <dl className="space-y-1.5">
            <Line label="Ask" value={s.ask} />
            <Line label="Done" value={s.done} />
            <Line label="Next" value={s.next} accent />
          </dl>
          {/* If the model ever stops producing the three labelled lines, show
              what it did say rather than an empty box. */}
          {!s.ask && !s.done && !s.next && s.raw && (
            <p className="whitespace-pre-line text-xs leading-relaxed text-ink">{s.raw}</p>
          )}
          <p className="mt-2 text-3xs leading-relaxed text-ink-tertiary">
            Written by a model from the thread below. It can be wrong — the thread is the
            record.
          </p>
        </>
      )}
    </section>
  );
}

function Line({
  label,
  value,
  accent,
}: {
  label: string;
  value: string | null;
  accent?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="flex gap-2">
      <dt
        className={cn(
          'w-9 shrink-0 pt-px text-3xs font-semibold uppercase tracking-wide',
          accent ? 'text-brand' : 'text-ink-tertiary',
        )}
      >
        {label}
      </dt>
      <dd className={cn('text-xs leading-relaxed', accent ? 'text-ink' : 'text-ink-secondary')}>
        {value}
      </dd>
    </div>
  );
}
